import { useState, useCallback } from 'react';
import { InspectionService } from '../services/inspections';
import { isInspectionBaseView } from '../features/inspections/inspectionViewConstants';

export type StillFramePayload = {
  camera: string;
  frameTimestamp: string;
};

export type StillFrameResponse = {
  imageUrl?: string;
  [key: string]: any;
};

type UseStillFrameReturn = {
  createStillFrame: (
    camera: string,
    frameTimestamp: string
  ) => Promise<StillFrameResponse>;
  clearStillFrame: () => void;
  loading: boolean;
  error: string | null;
  data: StillFrameResponse | null;
};

export const useStillFrame = (
  inspectionId: string
): UseStillFrameReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StillFrameResponse | null>(null);

  const clearStillFrame = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  const createStillFrame = useCallback(
    async (
      camera: string,
      frameTimestamp: string
    ): Promise<StillFrameResponse> => {
      try {
        setLoading(true);
        setError(null);

        // Clear old frame data before fetching new one
        setData(null);

        const payload: StillFramePayload = {
          camera: camera.replace(/\s+/g, ""),
          frameTimestamp,
        };

        const response: any =
          await InspectionService.createInspectionStillFrame(
            inspectionId,
            payload
          );

        const rawViewName = camera?.split('_')?.[0] || '';
        const viewName = isInspectionBaseView(rawViewName) ? rawViewName : 'Mobile';

        const updatedResponse: StillFrameResponse = {
          ...response,
          data: {
            ...response?.data,
            viewName: viewName,
            // Persist the captured video time so the damage created from this
            // frame stores its real videoFrameTime (enables server-side dedup of
            // near-duplicate frames on subsequent captures).
            videoFrameTime: Number(frameTimestamp),
          },
        };

        setData(updatedResponse);

        return updatedResponse;
      } catch (err: any) {
        const errorMessage =
          err?.message || 'Failed to create still frame';

        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [inspectionId]
  );

  return {
    createStillFrame,
    clearStillFrame,
    loading,
    error,
    data,
  };
};